import React, { useState, useEffect } from "react";
import { Col, Container, Row, Button, Form, Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import SingleDetailHeader from "../../components/singleDetail/SingleDetailHeader";
import BreadCrumb from "../../components/breadcrumb/BreadCrumb";
import Notification, { showToast } from "../../../components/common/notification/Notification";

const EditLeasePurpose = () => {
  const navigate = useNavigate();
  const { Id } = useParams();
  const authState = useSelector((state) => state.auth);
  const loggedInUser = authState?.user ?? authState;
  const token = loggedInUser?.refreshToken || authState?.refreshToken || loggedInUser?.token || authState?.token;
  const baseUrl = process.env.REACT_APP_BASE_URL;

  const [fetching, setFetching] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    id: Id,
    name: "",
    description: "",
  });

  useEffect(() => {
    const fetchPurposeDetails = async () => {
      setFetching(true);
      try {
        const response = await axios.get(`${baseUrl}/lease-purpose/${Id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const resData = response.data;
        const details =
          resData?.leasePurpose ||
          resData?.data ||
          resData?.lease_purpose ||
          (typeof resData === "object" ? resData : null);

        if (details) {
          setFormData({
            id: details.id || Id,
            name: details.name || "",
            description: details.description || "",
          });
        }
      } catch (error) {
        console.error("Error fetching lease purpose details:", error);
        showToast("Error fetching lease purpose details.", { type: "error" });
      } finally {
        setFetching(false);
      }
    };

    if (Id) {
      fetchPurposeDetails();
    }
  }, [Id, baseUrl, token]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      showToast("Please enter a name.", { type: "error" });
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.put(
        `${baseUrl}/lease-purpose/edit`,
        {
          id: Number(formData.id),
          name: formData.name,
          description: formData.description,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200 || response.data?.statusType === "SUCCESS") {
        showToast("Lease Purpose updated successfully!", { type: "success" });
        setTimeout(() => {
          navigate("/lease-purpose");
        }, 1200);
      } else {
        showToast(response.data?.text || "Failed to update Lease Purpose.", { type: "error" });
      }
    } catch (error) {
      console.error("Error updating lease purpose:", error);
      showToast("Error updating Lease Purpose. Please try again.", { type: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const breadcrumbItems = [
    { label: "", link: "/admin", icon: "fa-solid fa-house" },
    { label: "Lease Purpose", link: "/lease-purpose" },
    { label: "Edit Lease Purpose", link: `/lease-purpose/${Id}/edit`, active: true },
  ];

  if (fetching) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2 text-muted">Loading lease purpose details...</p>
      </div>
    );
  }

  return (
    <>
      <Notification />
      <div className="content-body2">
        <Container>
          <BreadCrumb items={breadcrumbItems} />
          <Row>
            <Col lg={12} md={12} sm={12}>
              <SingleDetailHeader
                header="Edit Lease Purpose"
                description="Update lease purpose details."
              />

              <Form onSubmit={handleSubmit} className="bg-white p-4 rounded shadow-sm border mt-3">
                <Form.Group controlId="formName" className="mb-3">
                  <Form.Label className="fw-semibold">Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="e.g. Commercial Lease"
                    required
                  />
                </Form.Group>

                <Form.Group controlId="formDescription" className="mb-4">
                  <Form.Label className="fw-semibold">Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Enter description..."
                  />
                </Form.Group>

                <div className="d-flex justify-content-end gap-2">
                  <Button variant="secondary" onClick={() => navigate("/lease-purpose")} disabled={submitting}>
                    Cancel
                  </Button>
                  <Button variant="primary" type="submit" disabled={submitting}>
                    {submitting ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Saving...
                      </>
                    ) : (
                      "Save Changes"
                    )}
                  </Button>
                </div>
              </Form>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default EditLeasePurpose;
